import type { ReactNode } from "react";

import type { NavItem } from "../../components/Sidebar/navTree";
import { visibleNavItems } from "../../components/Sidebar/navTree";
import { withRestoreParam } from "../table/restore";

/** A row that is a page. Group headers are not among them: they have no page of their own. */
export type NavRowId =
    | "dashboard"
    | "pendingActions"
    | "controlManagement"
    | "controlPrograms"
    | "controls"
    | "controlActivities"
    | "controlTesting"
    | "imperoShare"
    | "riskManagement"
    | "reports"
    | "userManagement"
    | "settings";

export type NavGroupId = "home" | "controlManagement";

export type NavId = NavRowId | NavGroupId;

export type NavIconName =
    | "home"
    | "controlManagement"
    | "controlTesting"
    | "imperoShare"
    | "riskManagement"
    | "reports"
    | "userManagement"
    | "settings";

export type NavFeatureFlag =
    | "controlTesting"
    | "imperoShare"
    | "controlEntities";

export type NavLabelKey =
    | "ControlManagement_ModuleTitle"
    | "ControlPrograms"
    | "ControlTemplate_MenuTitle"
    | "ControlTesting_ModuleTitle"
    | "Dasboard_MenuTitle"
    | "Navigation_Activities"
    | "Navigation_Controls"
    | "Navigation_Home"
    | "Navigation_PendingActions"
    | "Navigation_Settings"
    | "Report_MenuTitle"
    | "RiskModule_MenuTitle"
    | "User_MenuTitle";

export interface NavConfigItem {
    readonly id: NavId;
    readonly labelKey: NavLabelKey;
    readonly icon?: NavIconName;
    /** Absent on a group header, which hands off to its first child. */
    readonly href?: string;
    readonly featureFlag?: NavFeatureFlag;
    /** Greys the row out instead of dropping it, so the feature stays discoverable. */
    readonly lockedWhenFlagOff?: boolean;
    readonly children?: readonly NavConfigItem[];
}

export interface NavResolution {
    readonly t: (key: NavLabelKey) => string;
    readonly icons: Readonly<Record<NavIconName, ReactNode>>;
    readonly flags: Readonly<Record<NavFeatureFlag, boolean>>;
    readonly counts?: Partial<Readonly<Record<NavId, number>>>;
}

export const groupedNavConfig: readonly NavConfigItem[] = [
    {
        id: "home",
        labelKey: "Navigation_Home",
        icon: "home",
        children: [
            {
                id: "dashboard",
                labelKey: "Dasboard_MenuTitle",
                href: "/home",
            },
            {
                id: "pendingActions",
                labelKey: "Navigation_PendingActions",
                href: withRestoreParam("/dashboard-pending-activities"),
            },
        ],
    },
    {
        id: "riskManagement",
        labelKey: "RiskModule_MenuTitle",
        icon: "riskManagement",
        href: "/risks",
    },
    {
        id: "controlManagement",
        labelKey: "ControlManagement_ModuleTitle",
        icon: "controlManagement",
        children: [
            {
                id: "controlPrograms",
                labelKey: "ControlPrograms",
                href: "/controls",
            },
            {
                id: "controls",
                labelKey: "Navigation_Controls",
                href: "/controls/all",
            },
            {
                id: "controlActivities",
                labelKey: "Navigation_Activities",
                href: withRestoreParam("/control-activities"),
            },
        ],
    },
    {
        id: "controlTesting",
        labelKey: "ControlTesting_ModuleTitle",
        icon: "controlTesting",
        href: withRestoreParam("/testing-programs"),
        featureFlag: "controlTesting",
    },
    {
        id: "imperoShare",
        labelKey: "ControlTemplate_MenuTitle",
        icon: "imperoShare",
        href: "/v1/Share",
        featureFlag: "imperoShare",
    },
    {
        id: "reports",
        labelKey: "Report_MenuTitle",
        icon: "reports",
        href: "/reports",
    },
    {
        id: "userManagement",
        labelKey: "User_MenuTitle",
        icon: "userManagement",
        href: "/users",
    },
    {
        id: "settings",
        labelKey: "Navigation_Settings",
        icon: "settings",
        href: "/profile",
    },
];

export function resolveNavConfig(
    config: readonly NavConfigItem[],
    resolution: NavResolution
): readonly NavItem[] {
    return visibleNavItems(config.map((item) => resolveItem(item, resolution)));
}

function resolveItem(item: NavConfigItem, resolution: NavResolution): NavItem {
    const enabled =
        item.featureFlag === undefined || resolution.flags[item.featureFlag];

    return {
        id: item.id,
        label: resolution.t(item.labelKey),
        icon: item.icon === undefined ? undefined : resolution.icons[item.icon],
        href: item.href,
        count: resolution.counts?.[item.id],
        visible: enabled || item.lockedWhenFlagOff === true,
        locked: !enabled,
        children: item.children?.map((child) => resolveItem(child, resolution)),
    };
}
